// src/components/system/ExitPrompt.js
import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { Native } from "../../utils/native";

// Key groups
const LEFT_KEYS = new Set(["ArrowLeft", "Left"]);
const RIGHT_KEYS = new Set(["ArrowRight", "Right"]);
const ENTER_KEYS = new Set(["Enter", "OK", " "]);
const BACK_KEYS = new Set(["Back", "Backspace", "Escape", "BrowserBack", "GoBack"]);
const UP_KEYS = new Set(["ArrowUp", "Up"]);
const DOWN_KEYS = new Set(["ArrowDown", "Down"]);

// Shared persistent white ring for both buttons (always on)
const PERSISTENT_RING_SX = {
  position: "relative",
  border: "3px solid rgba(255,255,255,0.9)",           // persistent white border
  borderRadius: 2,                                      // matches Button's radius
  // Keep hover consistent
  "&:hover": {
    borderColor: "rgba(255,255,255,1)",
  },
  // Active focus indicator layered on top of the always-on border
  "&.focused, &:focus-visible": {
    outline: "3px solid rgba(255,255,255,1)",
    outlineOffset: 2,
    borderColor: "rgba(255,255,255,1)",
  },
};

export default function ExitPrompt({ open, onClose }) {
  const wrapRef = React.useRef(null);
  const cancelRef = React.useRef(null);
  const exitRef = React.useRef(null);
  const [idx, setIdx] = React.useState(0); // 0 = Cancel, 1 = Exit

  // local focus helper that keeps "focused" class inside the overlay
  const focusEl = React.useCallback((el) => {
    if (!el || !wrapRef.current) return;
    wrapRef.current.querySelectorAll(".focused").forEach((n) => n.classList.remove("focused"));
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
    el.classList.add("focused");
    el.focus({ preventScroll: true });
  }, []);

  const focusIdx = React.useCallback(
    (i) => {
      setIdx(i);
      focusEl(i === 0 ? cancelRef.current : exitRef.current);
    },
    [focusEl]
  );

  // When opening, put focus on "Cancel"
  React.useEffect(() => {
    if (!open) return;
    // small rAF to ensure node is mounted
    const id = requestAnimationFrame(() => focusIdx(0));
    return () => cancelAnimationFrame(id);
  }, [open, focusIdx]);

  // Trap DPAD inside the modal (capture so remote.js never sees it)
  React.useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      const k = e.key;

      // Always trap these while open
      if (
        LEFT_KEYS.has(k) ||
        RIGHT_KEYS.has(k) ||
        ENTER_KEYS.has(k) ||
        BACK_KEYS.has(k) ||
        UP_KEYS.has(k) ||
        DOWN_KEYS.has(k)
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
      }

      if (LEFT_KEYS.has(k)) return focusIdx(0);
      if (RIGHT_KEYS.has(k)) return focusIdx(1);
      if (UP_KEYS.has(k) || DOWN_KEYS.has(k)) {
        // Do nothing but keep focus here
        return;
      }
      if (BACK_KEYS.has(k)) return onClose?.();

      if (ENTER_KEYS.has(k)) {
        if (idx === 0) onClose?.();
        else Native.exitApp();
      }
    };

    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, idx, focusIdx, onClose]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0,0,0,0.65)",
        display: "grid",
        placeItems: "center",
      }}
      data-scope="overlay"
      aria-modal="true"
      role="dialog"
    >
      <Box
        ref={wrapRef}
        sx={{
          width: 720,
          borderRadius: 18,
          p: 5,
          bgcolor: "rgba(10,10,14,0.96)",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 24px 96px rgba(0,0,0,0.7)",
        }}
      >
        <Typography
          variant="h2"
          sx={{ mb: 2.5, fontWeight: 900, fontSize: 40, lineHeight: 1.1 }}
        >
          Exit Kbean TV?
        </Typography>

        <Typography
          variant="h6"
          sx={{ opacity: 0.9, mb: 4, fontSize: 20, lineHeight: 1.35 }}
        >
          Press left or right to choose, then press Enter.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          <Button
            ref={cancelRef}
            variant="outlined"
            size="large"
            onClick={onClose}
            sx={{
              ...PERSISTENT_RING_SX,
              fontWeight: 900,
              minWidth: 200,
              py: 1.25,
              // Keep outlined visual while enforcing our border
              border: "3px solid white",
              borderRadius: "999px",
              backgroundColor: "transparent",
            }}
          >
            Cancel
          </Button>

          <Button
            ref={exitRef}
            variant="contained"
            size="large"
            color="error"
            onClick={() => Native.exitApp()}
            sx={{
              ...PERSISTENT_RING_SX,
              fontWeight: 900,
              minWidth: 200,
              py: 1.25,
              borderRadius: "999px",
            }}
          >
            Exit
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
